import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CreditCard, Wallet, ShoppingCart, Loader, AlertCircle } from "lucide-react";
import {
  buildConsumerRuntimeOrderPayUrl,
  buildConsumerRuntimeHeaders,
  resolveConsumerRuntimeApiBase,
  resolveConsumerRuntimeConnectionUuid,
  resolveSellingPointId,
} from "../runtime-endpoints";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import { trn } from "../shared/i18n";
import { saveConsumerOrderSession } from "../orderSession";
import { clearStoredCart } from "../cart-storage";

type CartItem = {
  id?: string | number;
  productId?: string | number;
  name?: string;
  label?: string;
  price: number | string;
  quantity: number;
  comment?: string | null;
  note?: string | null;
  options?: Array<Record<string, unknown>>;
  menuSectionId?: number | string;
  connectionId?: number | string;
  locationId?: number | string;
};

type TerminalConfig = {
  paymentTerminalEnabled?: boolean;
  paymentTerminalType?: string | null;
  acceptedPaymentMethods?: string[];
  onlinePaymentEnabled?: boolean;
  onlinePaymentProvider?: string | null;
};

type NativePaymentBridge = {
  postOrderActions?: (payload: string) => unknown;
  startCardPayment?: (payload: string) => unknown;
  processCardPayment?: (payload: string) => unknown;
  requestCardPayment?: (payload: string) => unknown;
};

function normalizeAcceptedPaymentMethod(method: unknown): "card_present" | "online_qr" | "cash" | "tab" | null {
  const raw = String(method ?? "").trim().toLowerCase();
  if (!raw) return null;
  if (["card_present", "card", "credit_card", "external_payment", "physical_card"].includes(raw)) {
    return "card_present";
  }
  if (["online_qr", "online", "qr", "qr_code", "credit_card_online", "redsys", "stripe"].includes(raw)) {
    return "online_qr";
  }
  if (["cash", "counter", "pay_at_counter", "pay-counter", "pay_counter", "pay_in_store", "pay_at_store", "efectivo"].includes(raw)) {
    return "cash";
  }
  if (["tab", "pay_later", "paylater", "pay_at_table"].includes(raw)) return "tab";
  return null;
}

function resolveAcceptedPaymentMethods(terminalConfig: TerminalConfig): Array<"card_present" | "online_qr" | "cash" | "tab"> {
  const configured = Array.isArray(terminalConfig?.acceptedPaymentMethods)
    ? terminalConfig.acceptedPaymentMethods
        .map(normalizeAcceptedPaymentMethod)
        .filter((method): method is "card_present" | "online_qr" | "cash" | "tab" => Boolean(method))
    : [];
  const methods = configured.length
    ? configured
    : [
        ...(terminalConfig?.paymentTerminalEnabled ? (["card_present"] as const) : []),
        "cash" as const,
        ...(terminalConfig?.onlinePaymentEnabled ? (["online_qr"] as const) : []),
      ];

  if (!methods.includes("cash")) methods.push("cash");
  return Array.from(new Set(methods));
}

function readTerminalConfig(): TerminalConfig {
  try {
    const raw = localStorage.getItem("terminalConfig");
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TerminalConfig;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readNativePaymentBridge(): NativePaymentBridge | null {
  const host = window as typeof window & {
    VolqanNativeAPI?: NativePaymentBridge;
    Android?: NativePaymentBridge;
  };
  const bridge =
    (host.VolqanNativeAPI && typeof host.VolqanNativeAPI === "object" ? host.VolqanNativeAPI : null) ||
    (host.Android && typeof host.Android === "object" ? host.Android : null);
  return bridge && typeof bridge === "object" ? bridge : null;
}

function hasNativeCardPaymentSupport(bridge: NativePaymentBridge | null): boolean {
  if (!bridge) return false;
  return (
    typeof bridge.postOrderActions === "function" ||
    typeof bridge.startCardPayment === "function" ||
    typeof bridge.processCardPayment === "function" ||
    typeof bridge.requestCardPayment === "function"
  );
}

export default function Payment({
  cart,
  setCart,
}: {
  cart: CartItem[];
  setCart: (cart: CartItem[]) => void;
}) {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = readConsumerRuntimeTableCode();
  const isTableMode = deviceMode === "table";
  const terminalConfig = useMemo(() => readTerminalConfig(), []);
  const acceptedPaymentMethods = useMemo(() => resolveAcceptedPaymentMethods(terminalConfig), [terminalConfig]);

  const sellingPointId = useMemo(
    () => resolveSellingPointId(),
    []
  );
  const connectionUuid = useMemo(
    () => resolveConsumerRuntimeConnectionUuid(),
    []
  );

  // Total calculado en cliente (el servidor recalcula)
  const totalAmount = useMemo(
    () =>
      cart
        .reduce(
          (acc, it) =>
            acc + (Number(it.price) || 0) * (Number(it.quantity) || 0),
          0
        )
        .toFixed(2),
    [cart]
  );

  // ---- Bridge de callbacks Android (igual que tenías) ----
  useEffect(() => {
    const log = (
      type: "log" | "warn" | "error",
      label: string,
      data: any
    ) => {
      const timestamp = new Date().toISOString();
      const prefix = `📣 [React] ${label} @ ${timestamp}`;
      const message = typeof data === "string" ? data : JSON.stringify(data);
      console[type](`${prefix} →`, message);
    };

    (window as any).handlePaymentCompleted = (data: any) => {
      log("log", "handlePaymentCompleted called", data);
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        log("log", "Parsed payment_completed", parsed);
        setIsWaitingForPayment(false);
        setCart([]);
        clearStoredCart();
        navigate(`/order-confirmation?orderId=${parsed.orderId}`);
      } catch (e) {
        log("error", "Error parsing payment_completed", e);
      }
    };

    (window as any).handlePaymentCompletedWithPrintError = (data: any) => {
      log("warn", "handlePaymentCompletedWithPrintError called", data);
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        log("warn", "Parsed payment_completed_but_print_failed", parsed);
        setIsWaitingForPayment(false);
        setCart([]);
        clearStoredCart();
        navigate(
          `/order-confirmation?orderId=${parsed.orderId}&printerError=true`
        );
      } catch (e) {
        log("error", "Error parsing payment_completed_but_print_failed", e);
      }
    };

    (window as any).handlePaymentFailed = (data: any) => {
      log("warn", "handlePaymentFailed called", data);
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        log("warn", "Parsed payment_failed", parsed);
        setIsWaitingForPayment(false);
        setIsProcessing(false);
        setError(parsed.error || trn("app.consumer.payment.failed_retry", "Payment failed. Try again."));
      } catch (e) {
        log("error", "Error parsing payment_failed", e);
      }
    };

    return () => {
      delete (window as any).handlePaymentCompleted;
      delete (window as any).handlePaymentCompletedWithPrintError;
      delete (window as any).handlePaymentFailed;
    };
  }, [navigate, setCart]);

  // Normaliza items al shape que espera el endpoint local de consumer-runtime
  function mapItemsForApi(items: CartItem[]) {
    return (items || []).map((it) => {
      const unit = Number(it.price) || 0;
      const qty = Number(it.quantity) || 0;
      return {
        id: String(it.id ?? it.productId ?? ""),
        productId: String(it.productId ?? it.id ?? ""),
        name: String(it.name ?? it.label ?? trn("app.consumer.common.product", "Product")),
        price: unit,
        quantity: qty,
        comment: it.note ?? it.comment ?? null,
        options: Array.isArray(it.options) ? it.options : [],
        menuSectionId: it.menuSectionId ? String(it.menuSectionId) : undefined,
        connectionId: it.connectionId ? String(it.connectionId) : undefined,
        locationId: it.locationId ? String(it.locationId) : undefined,
      };
    });
  }

  function buildKioskHeaders() {
    const headers: Record<string, string> = {
      ...buildConsumerRuntimeHeaders(connectionUuid),
      "Content-Type": "application/json; charset=UTF-8",
      "x-lang": localStorage.getItem("volqan.consumer.language") || "es",
    };
    return headers;
  }

  function getCardPaymentUnavailableMessage() {
    return trn(
      "app.consumer.payment.card_unavailable",
      "Card payment is not available on this device."
    );
  }

  const confirmOrder = async (
    paymentMethod: "CARD" | "CARD_PRESENT" | "CASH" | "TAB" | "ONLINE_QR"
  ) => {
    if (!cart?.length) {
      alert(trn("app.consumer.payment.empty_cart", "The cart is empty."));
      return;
    }

    if (!sellingPointId) {
      setError(trn("app.consumer.payment.missing_terminal", "Device is not configured. Missing selling point."));
      return;
    }
    if (isTableMode && !tableCode) {
      setError(trn("app.consumer.payment.missing_table", "Missing configured table for this device."));
      return;
    }

    setIsProcessing(true);
    setError(null);

    const payload = {
      orderType: isTableMode ? "table" : "collection",
      executionTime: "asap",
      payments:
        paymentMethod === "TAB"
          ? []
          : [{ methodCode: paymentMethod, amount: Number(totalAmount) }],
      items: mapItemsForApi(cart),
      printer: {
        address: (localStorage.getItem("printerIp") || "").trim() || undefined,
      },
      description: isTableMode
        ? trn("app.consumer.payment.table_order_description", "Table order {tableCode}", {
            tableCode: tableCode || "?",
          })
        : trn("app.consumer.payment.kiosk_order_description", "Kiosk order"),
      currency: "EUR",
      customerName: trn("app.consumer.payment.anonymous_customer", "Anonymous"),
      sellingPointId,
      tableCode: isTableMode ? tableCode : undefined,
      tableName: isTableMode ? tableCode : undefined,
      serviceMode: isTableMode ? "dinein_table" : "kiosk",
      channel: isTableMode ? "table" : "kiosk",
      sourceApp: isTableMode ? "table-app" : "kiosk-app",
    };

    try {
      const headers = buildKioskHeaders();

      const res = await axios.post(`${resolveConsumerRuntimeApiBase(sellingPointId)}/orders`, payload, {
        headers,
      });
      const data = res?.data?.data || res?.data || {};
      console.log("✅ Pedido creado (consumer-runtime):", data);
      const orderId = String(data?.id || data?.orderId || "").trim();
      const friendlyCode = String(data?.friendlyCode || data?.publicOrderNumber || "").trim();
      const saleId = String(data?.sale?.saleId || data?.saleId || orderId).trim();
      const payUrl = String(data?.payUrl || buildConsumerRuntimeOrderPayUrl(orderId)).trim();
      const marketWebBaseUrl = String(data?.marketWebBaseUrl || "").trim();
      const selectedPaymentMode =
        paymentMethod === "TAB"
          ? "tab"
          : paymentMethod === "CASH"
            ? "cash"
            : paymentMethod === "ONLINE_QR"
              ? "online_qr"
              : "card_present";
      if (orderId && saleId && sellingPointId) {
        saveConsumerOrderSession({
          orderId,
          friendlyCode,
          saleId,
          sellingPointId,
          marketWebBaseUrl,
          currency: String(data?.currency || payload.currency || "EUR"),
          amount: Number(data?.amount || totalAmount) || 0,
          createdAt: new Date().toISOString(),
          mode: isTableMode ? "table" : "kiosk",
          paymentMode: selectedPaymentMode,
          payUrl: payUrl || "",
        });
      }
      const nativePaymentBridge = readNativePaymentBridge();
      const canHandleCardPayment =
        Boolean(terminalConfig?.paymentTerminalEnabled) &&
        hasNativeCardPaymentSupport(nativePaymentBridge);

      // Intento de impresión en Android (si existe)
      let androidPostOrderFailed = false;
      if (!data?.requiresOnlinePayment && typeof nativePaymentBridge?.postOrderActions === "function") {
        try {
          nativePaymentBridge.postOrderActions(JSON.stringify(data));
        } catch (e) {
          console.error("❌ Error en postOrderActions:", e);
          androidPostOrderFailed = true;
        }
      } else {
        androidPostOrderFailed = true;
      }

      if (data?.requiresPhysicalPayment) {
        // Flujo con TPV físico (CARD)
        if (!canHandleCardPayment) {
          setError(getCardPaymentUnavailableMessage());
          setIsProcessing(false);
          setIsWaitingForPayment(false);
          return;
        }
        setIsProcessing(false);
        setIsWaitingForPayment(true);
        // Espera a callbacks handlePaymentCompleted/Failed
        return;
      }

      if (data?.requiresOnlinePayment) {
        setCart([]);
        clearStoredCart();
        setIsProcessing(false);
        navigate(
          `/order-confirmation?orderId=${encodeURIComponent(orderId)}&payment=online_qr${
            isTableMode ? "&mode=table" : ""
          }`
        );
        return;
      }

      // Flujo sin TPV (CASH u otros)
      setCart([]);
      clearStoredCart();
      if (androidPostOrderFailed) {
        navigate(
          `/order-confirmation?orderId=${
            data?.id || data?.orderId
          }&printerError=true${isTableMode ? "&mode=table" : ""}`
        );
      } else {
        navigate(`/order-confirmation?orderId=${data?.id || data?.orderId}${isTableMode ? "&mode=table" : ""}`);
      }
    } catch (err: any) {
      const errorCode = err?.response?.data?.errors?.[0]?.code;
      console.error(
        "❌ Error al enviar pedido:",
        err?.response?.data || err?.message || err
      );
      setError(
        errorCode === "UNAUTHORIZED"
          ? trn("app.consumer.payment.submit_error", "Could not send the order. Try again.")
          : errorCode === "DATA_GATEWAY_TIMEOUT" || errorCode === "DATA_GATEWAY_UNAVAILABLE"
          ? trn(
              "app.consumer.payment.service_unavailable",
              "The ordering service is temporarily unavailable. Please try again in a moment."
            )
          : errorCode === "SELLING_POINT_ID_REQUIRED"
          ? trn("app.consumer.payment.missing_terminal", "Device is not configured. Missing selling point.")
          : errorCode === "PAYMENT_TERMINAL_UNAVAILABLE"
          ? getCardPaymentUnavailableMessage()
          : errorCode === "MENU_SNAPSHOT_NOT_FOUND" || err?.response?.data?.error === "menu_not_found"
          ? trn(
              "app.consumer.payment.menu_snapshot_missing",
              "There is no menu snapshot for this device. Upload a menu and mark it as current."
            )
          : err?.response?.data?.errors?.[0]?.message
          ? err.response.data.errors[0].message
          : trn("app.consumer.payment.submit_error", "Could not send the order. Try again.")
      );
      setIsProcessing(false);
      setIsWaitingForPayment(false);
    }
  };



  return (
    <div className="fullscreen-container-dark">
      <div className="fullscreen-content">


        {isProcessing && (
          <div className="d-flex flex-column align-items-center justify-content-center text-center">
            <Loader size={50} className="animate-spin text-primary mb-3" />
            <h2 className="fs-2 fw-bold">
              {trn("app.consumer.payment.checking_order", "Checking order")}
              <span className="loading-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </h2>
          </div>
        )}

        {isWaitingForPayment && (
          <div className="d-flex flex-column align-items-center justify-content-center text-center">
            <Loader size={50} className="animate-spin text-warning mb-3" />
            <h2 className="fs-2 fw-bold">
              {trn("app.consumer.payment.waiting_card", "Waiting for card payment")}
              <span className="loading-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </h2>
            <button
              className="btn btn-danger btn-lg mt-4 d-flex align-items-center justify-content-center gap-3"
              onClick={() => {
                setIsWaitingForPayment(false);
                setIsProcessing(false);
                setError(null);
                navigate("/cart");
              }}
            >
              {trn("app.consumer.common.cancel", "Cancel")}
            </button>
          </div>
        )}

        {!isProcessing && !isWaitingForPayment && (
          <>
            <h1 className="fw-bold mb-4">
              {isTableMode
                ? trn("app.consumer.payment.table_order_title", "Table order")
                : trn("app.consumer.payment.title", "Payment")}
            </h1>
            <p className="fs-3">
              {isTableMode
                ? trn("app.consumer.payment.table_label", "Table {tableCode}", { tableCode: tableCode || "?" })
                : trn("app.consumer.payment.total_to_pay", "Total to pay:")}
            </p>
            <h2 className="fw-bold fs-1 dq-price-text">{totalAmount} €</h2>

            {error && (
              <div className="alert alert-danger mt-3 d-flex flex-column align-items-center">
                <AlertCircle size={50} className="text-danger mb-2" />
                <p className="fs-4 fw-bold">{error}</p>
                <button
                  className="btn btn-light btn-lg w-100 mt-3"
                  onClick={() => {
                    setError(null);
                    setIsWaitingForPayment(false);
                    setIsProcessing(false);
                  }}
                >
                  {trn("app.consumer.payment.retry", "Retry payment")}
                </button>
                <button
                  className="btn btn-secondary btn-lg w-100 mt-3"
                  onClick={() => navigate("/cart")}
                >
                  {trn("app.consumer.common.back", "Back")}
                </button>
              </div>
            )}

            {!error && (
              <>
                {isTableMode ? (
                  <button
                    className="btn btn-primary btn-lg w-100 mt-4 d-flex align-items-center justify-content-center gap-3"
                    onClick={() => confirmOrder("TAB")}
                    disabled={isProcessing}
                  >
                    <ShoppingCart size={32} /> {trn("app.consumer.payment.send_to_kitchen", "Send order to kitchen")}
                  </button>
                ) : (
                  <>
                    {acceptedPaymentMethods.includes("card_present") ? (
                      <button
                        className="btn btn-primary btn-lg w-100 mt-4 d-flex align-items-center justify-content-center gap-3"
                        onClick={() => confirmOrder("CARD_PRESENT")}
                        disabled={isProcessing}
                      >
                        <CreditCard size={32} /> {trn("app.consumer.payment.pay_card", "Pay by card")}
                      </button>
                    ) : null}
                    {acceptedPaymentMethods.includes("online_qr") ? (
                      <button
                        className="btn btn-info btn-lg w-100 mt-3 d-flex align-items-center justify-content-center gap-3"
                        onClick={() => confirmOrder("ONLINE_QR")}
                        disabled={isProcessing}
                      >
                        <CreditCard size={32} />{" "}
                        {trn(
                          "app.consumer.payment.pay_mobile",
                          "Pay with your phone"
                        )}
                      </button>
                    ) : null}

                    {acceptedPaymentMethods.includes("cash") ? (
                      <button
                        className="btn btn-warning consumer-payment-counter btn-lg w-100 mt-3 d-flex align-items-center justify-content-center gap-3"
                        onClick={() => confirmOrder("CASH")}
                        disabled={isProcessing}
                      >
                        <Wallet size={32} /> {trn("app.consumer.payment.pay_counter", "Pay at counter")}
                      </button>
                    ) : null}
                  </>
                )}

                <button
                  className="btn btn-light btn-lg w-100 mt-3 d-flex align-items-center justify-content-center gap-3"
                  onClick={() => navigate("/cart")}
                  disabled={isProcessing}
                >
                  <ShoppingCart size={32} /> {trn("app.consumer.common.back", "Back")}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
